import { getMyOrganizationsQueryOptions } from "@packages/api-client/hooks/organizationController";
import { useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute, useSearch } from "@tanstack/react-router";
import { Building2, type LucideIcon, User } from "lucide-react";
import { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { PageHeader } from "@/components/PageHeader";
import {
	type NavigationItem,
	SideNavigation,
} from "@/components/SideNavigation";
import { type TabItem, TabNavigation } from "@/components/TabNavigation";
import { OrganizationsSettings } from "./-components/OrganizationsSettings";
import { ProfileSettings } from "./-components/ProfileSettings";

type SettingsSection = "profile" | "organizations";

export const Route = createFileRoute("/_auth/app/_app/user-settings/")({
	validateSearch: (search: Record<string, unknown>): { tab?: string } => {
		return {
			tab: typeof search.tab === "string" ? search.tab : undefined,
		};
	},
	loader: async ({ context }) => {
		// Preload user organizations data
		context.queryClient.ensureQueryData(getMyOrganizationsQueryOptions());
	},
	component: RouteComponent,
});

interface SettingsNavItem {
	key: SettingsSection;
	label: string;
	icon: LucideIcon;
	search: Record<string, unknown>;
}

function RouteComponent() {
	const { t } = useTranslation();
	const { tab } = useSearch({ from: "/_auth/app/_app/user-settings/" });

	// Determine active section from URL search params, default to "organizations"
	const activeSection: SettingsSection =
		tab === "profile" ? "profile" : "organizations";

	// Preload organizations data
	useSuspenseQuery(getMyOrganizationsQueryOptions());

	// Reset the content scroll when switching sections so a long Organizations
	// list doesn't leave Profile scrolled halfway down.
	const contentRef = useRef<HTMLElement>(null);
	useEffect(() => {
		// Reading activeSection keeps it as an effect dependency (mirrors
		// OrgBaseLayout's scroll-reset idiom).
		if (activeSection) {
			contentRef.current?.scrollTo({ top: 0 });
		}
	}, [activeSection]);

	// Single source of truth for the two sections, mapped into both the desktop
	// sidebar and the mobile tab bar so labels never drift between them.
	const items: SettingsNavItem[] = [
		{
			key: "profile",
			label: t("userSettings.profile"),
			icon: User,
			search: { tab: "profile" },
		},
		{
			key: "organizations",
			label: t("userSettings.organizations"),
			icon: Building2,
			// Organizations is the default section — clear the tab param.
			search: {},
		},
	];

	const sidebarItems: NavigationItem[] = items.map((item) => ({
		key: item.key,
		label: item.label,
		icon: item.icon,
		to: "/app/user-settings",
		search: item.search,
	}));

	const tabItems: TabItem[] = items.map(
		({ key, label, icon: Icon, search }) => ({
			key,
			label,
			to: "/app/user-settings",
			search,
			icon: <Icon className="h-4 w-4" />,
		}),
	);

	const renderContent = () => {
		switch (activeSection) {
			case "profile":
				return <ProfileSettings />;
			default:
				return <OrganizationsSettings />;
		}
	};

	return (
		// Match the org layout's height model: full viewport on mobile (no app
		// header there), minus the 4rem fixed header on desktop. Without this the
		// page ran 4rem too tall and the inner scroll never engaged.
		<div className="flex flex-col h-[100dvh] md:h-[calc(100vh-4rem)] bg-base-200 overflow-hidden">
			{/* Pinned header: back button + title, with the mobile section switcher
			    tucked underneath so it stays reachable while content scrolls. */}
			<div className="shrink-0 bg-base-100 border-b border-base-300 p-3 md:p-4">
				<PageHeader
					title={t("userSettings.title")}
					showBackButton
					backButtonText={t("common.back")}
				/>
				{/* Mobile: horizontal tabs replace the cramped icon rail and free the
				    full viewport width for content. */}
				<div className="mt-3 md:hidden">
					<TabNavigation tabs={tabItems} activeTab={activeSection} />
				</div>
			</div>

			{/* min-h-0 lets this row shrink inside the flex column so the content
			    pane below can own the scroll instead of overflowing the viewport. */}
			<div className="flex flex-1 min-h-0 overflow-hidden">
				{/* Desktop: vertical sidebar. Wrapped in a desktop-only container so
				    SideNavigation's own mobile icon-rail variant never renders here. */}
				<div className="hidden md:flex md:h-full">
					<SideNavigation
						navigationItems={sidebarItems}
						activeTab={activeSection}
						testId="user-settings-navigation"
					/>
				</div>

				{/* The single scroll container for the page content. */}
				<main
					ref={contentRef}
					className="flex-1 min-h-0 overflow-auto overscroll-contain bg-base-200 pb-[env(safe-area-inset-bottom)]"
					data-testid="user-settings-content"
				>
					{renderContent()}
				</main>
			</div>
		</div>
	);
}
